import { useMutation } from '@tanstack/react-query';
import { FileDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Can } from '@nucleus-commerce/admin-kit/permissions';
import type {
  GetAdminV1StoresStoreIdReportsExportParams,
  ReportExportKind,
} from '@nucleus-commerce/api-client/admin';
import { Button } from '@nucleus-commerce/ui/components/button';

import { reportsApi } from '../api/reports-api';
import type { DayRange } from '../schemas/report-range';

export interface ReportExportButtonProps {
  storeId: string;
  report: ReportExportKind;
  /** Omitted for inventory, which is a snapshot of today. */
  range?: DayRange;
  interval?: GetAdminV1StoresStoreIdReportsExportParams['interval'];
}

function save(csv: string, filename: string) {
  const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

/** Downloads the report as the backend renders it in CSV, with the same figures as on screen. */
export function ReportExportButton({ storeId, report, range, interval }: ReportExportButtonProps) {
  const { t } = useTranslation('reports');
  const download = useMutation({
    mutationFn: () =>
      reportsApi.exportCsv(storeId, {
        report,
        ...(range ? { from: range.from, to: range.to } : {}),
        ...(interval ? { interval } : {}),
      }),
    onSuccess: (csv) =>
      save(csv, range ? `${report}-${range.from}-${range.to}.csv` : `${report}.csv`),
  });
  return (
    <Can permission="reports.read">
      <Button
        type="button"
        variant="outline"
        size="sm"
        disabled={download.isPending}
        onClick={() => download.mutate()}
        data-tour="reports.export"
      >
        <FileDown aria-hidden />
        {download.isPending ? t('export.preparing') : t('export.csv')}
      </Button>
    </Can>
  );
}
